import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Logo from '../components/Logo';
import Avatar from '../components/Avatar';
import { compact, softFloor } from '../services/publicStats';
import { LOMI_STATS } from '../data/lomiStats';
import { LOMI_TESTIMONIALS } from '../data/lomiTestimonials';
import './LandingRedesign.css';

/**
 * Landing redesign — "The Answer Sheet" (Fresh citrus, mobile-first), FULL PAGE.
 *
 * Mounted at /redesign for review against the live LandingV2. Grounded in real
 * product truth: past-exam practice dominates usage, Ask Lomi fires when
 * students are stuck, students study in Amharic/Afaan Oromo, and most reach
 * Lomi on Telegram. Numbers are STATIC (src/data/lomiStats.ts); testimonials
 * are real & verbatim (src/data/lomiTestimonials.ts). TRUST-FIRST: reach
 * numbers only, no efficacy %, no public roadmap.
 */

// Minimal line icons (no emoji) — one visual system across the feature grid.
const Icon: React.FC<{ name: string }> = ({ name }) => {
  const p = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  const paths: Record<string, React.ReactNode> = {
    paper: <><path {...p} d="M6 3h9l3 3v15H6z" /><path {...p} d="M9 9h6M9 13h6M9 17h4" /></>,
    chat: <><path {...p} d="M4 5h16v11H9l-5 4z" /><path {...p} d="M8 10h8M8 13h5" /></>,
    note: <><path {...p} d="M5 4h14v16l-4-3H5z" /><path {...p} d="M9 9h6M9 12h4" /></>,
    target: <><circle {...p} cx="12" cy="12" r="8" /><circle {...p} cx="12" cy="12" r="3.4" /></>,
    globe: <><circle {...p} cx="12" cy="12" r="8.5" /><path {...p} d="M3.5 12h17M12 3.5c3 3 3 14 0 17M12 3.5c-3 3-3 14 0 17" /></>,
    sun: <><circle {...p} cx="12" cy="12" r="4" /><path {...p} d="M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M18.4 5.6L17 7M7 17l-1.4 1.4" /></>,
    trophy: <><path {...p} d="M7 4h10v4a5 5 0 0 1-10 0z" /><path {...p} d="M7 6H4v1a3 3 0 0 0 3 3M17 6h3v1a3 3 0 0 1-3 3M9 15h6M10 15v3M14 15v3M8 21h8" /></>,
  };
  return <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden>{paths[name]}</svg>;
};

const FEATURES = [
  { icon: 'paper', title: 'Every real past exam', body: 'Practise thousands of genuine ESSLCE questions from years of real past papers — not made-up ones.' },
  { icon: 'chat', title: 'Ask Lomi, anytime', body: 'Stuck on a question at midnight? Tap it. Lomi explains it clearly and patiently — and never gets tired.' },
  { icon: 'note', title: 'Short notes & ESSLCE tips', body: 'Quick revision notes, key terms, and entrance-style quizzes to lock in a topic fast.' },
  { icon: 'target', title: 'Learns your weak spots', body: 'Lomi tracks what you get wrong and brings it back later — until it finally sticks.' },
  { icon: 'globe', title: 'In your language', body: 'Explanations in Amharic and Afaan Oromo, not only English. Study the way you think.' },
  { icon: 'trophy', title: 'Streaks, XP & rank', body: 'Keep a daily streak, earn XP, and climb the leaderboard against students across the country.' },
];

const STEPS = [
  { n: 'A', title: 'Practise real past exams', body: 'Start with actual ESSLCE questions from past papers — the exact patterns that come up on exam day.' },
  { n: 'B', title: 'Ask the moment you’re stuck', body: 'Tap any question and Lomi breaks it down — in Amharic, Afaan Oromo, or English — until it clicks.' },
  { n: 'C', title: 'Master what you miss', body: 'Lomi brings the questions you got wrong back, days later, so they turn into ones you’ll never miss again.' },
];

const LandingRedesign: React.FC = () => {
  const navigate = useNavigate();
  const [filled, setFilled] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduce) { setFilled(true); return; }
    const t = setTimeout(() => setFilled(true), 900);
    return () => clearTimeout(t);
  }, []);

  const options = [
    { key: 'A', text: 'Carotene', correct: false },
    { key: 'B', text: 'Chlorophyll', correct: true },
    { key: 'C', text: 'Xanthophyll', correct: false },
    { key: 'D', text: 'Anthocyanin', correct: false },
  ];

  const proof = [
    { value: softFloor(LOMI_STATS.students), label: 'students' },
    { value: `${(Math.floor(LOMI_STATS.schools / 100) * 100).toLocaleString()}+`, label: 'schools reached' },
    { value: compact(LOMI_STATS.questionsAnswered), label: 'questions practised' },
  ];

  const trust = ['100% free', 'Amharic · Afaan Oromo · English', 'Works on any phone', 'Web & Telegram'];

  return (
    <div className="lomi-redesign">
      {/* Top bar */}
      <header className="rd-nav">
        <Logo height={34} withAmharic className="rd-logo" onClick={() => navigate('/')} />
        <nav className="rd-nav-links">
          <button onClick={() => navigate('/testimonials')}>Stories</button>
          <button onClick={() => navigate('/blog')}>Blog</button>
          <button className="rd-nav-cta" onClick={() => navigate('/login')}>Start free</button>
        </nav>
      </header>

      {/* Hero */}
      <section className="rd-hero">
        <div className="rd-hero-copy">
          <span className="rd-eyebrow">
            <span className="rd-eyebrow-dot" aria-hidden />
            AI-native exam prep · Ethiopia
          </span>
          <h1 className="rd-title">
            Beat the<br />
            <span className="rd-title-accent">Entrance&nbsp;Exam.</span>
          </h1>
          <p className="rd-sub">
            <strong>Lomi (ሎሚ)</strong> is Ethiopia's AI tutor for the ESSLCE —
            trained on every past paper. It teaches, quizzes, and explains anything
            you're stuck on. <span className="rd-free">Free.</span>
          </p>
          <div className="rd-cta-row">
            <button className="rd-cta-primary" onClick={() => navigate('/login')}>
              <span className="rd-bubble-mini" aria-hidden />
              Start free
              <span className="rd-cta-arrow" aria-hidden>→</span>
            </button>
            <a className="rd-cta-secondary" href="https://t.me/lomistudy" target="_blank" rel="noopener noreferrer">
              or a daily question on Telegram
            </a>
          </div>
          <dl className="rd-proof">
            {proof.map((pr) => (
              <div className="rd-proof-item" key={pr.label}>
                <dt className="rd-proof-value">{pr.value}</dt>
                <dd className="rd-proof-label">{pr.label}</dd>
              </div>
            ))}
          </dl>
        </div>

        {/* Signature: the answer sheet */}
        <div className="rd-hero-visual">
          <div className="rd-card" ref={cardRef}>
            <div className="rd-card-head">
              <span className="rd-qnum">42</span>
              <span className="rd-qmeta">Biology · ESSLCE 2016 E.C.</span>
            </div>
            <p className="rd-question">
              Which pigment is primarily responsible for absorbing light during photosynthesis?
            </p>
            <ul className="rd-options" role="list">
              {options.map((o) => {
                const isFilled = filled && o.correct;
                return (
                  <li key={o.key} className={`rd-option${isFilled ? ' is-correct' : ''}`}>
                    <span className={`rd-bubble${isFilled ? ' filled' : ''}`} aria-hidden>
                      <span className="rd-bubble-letter">{o.key}</span>
                      {isFilled && <span className="rd-bubble-check">✓</span>}
                    </span>
                    <span className="rd-option-text">{o.text}</span>
                  </li>
                );
              })}
            </ul>
            <div className={`rd-lomi-note${filled ? ' show' : ''}`}>
              <span className="rd-lomi-face" aria-hidden>🍋</span>
              <span><strong>B — chlorophyll.</strong> A and C are accessory pigments — a classic ESSLCE trap.</span>
            </div>
          </div>
        </div>
      </section>

      {/* Trust signals */}
      <section className="rd-trust" aria-label="Why students trust Lomi">
        <ul className="rd-trust-list" role="list">
          {trust.map((t) => (
            <li key={t} className="rd-trust-item">
              <span className="rd-trust-check" aria-hidden>✓</span>{t}
            </li>
          ))}
        </ul>
      </section>

      {/* Problem — honest, human */}
      <section className="rd-problem">
        <div className="rd-problem-inner">
          <h2 className="rd-problem-title">
            The exam that decides your future — and often, no one to ask.
          </h2>
          <p className="rd-problem-body">
            The ESSLCE is the gate to university. But in a classroom of sixty, one teacher can't
            reach everyone — and when you're stuck at 11pm the night before, there's usually no
            one to explain it. Not because you're not smart. Because you're on your own.
          </p>
          <p className="rd-problem-punch">
            Lomi gives every student a patient tutor who has all the time in the world.
            <span className="rd-problem-punch-am">ሎሚ እዚህ ነው።</span>
          </p>
        </div>
      </section>

      {/* How it works — the loop, answer-sheet motif */}
      <section className="rd-how">
        <div className="rd-section-head">
          <span className="rd-kicker">How it works <span className="rd-kicker-am">· እንዴት እንደሚሰራ</span></span>
          <h2>Three steps between you and a pass.</h2>
        </div>
        <ol className="rd-steps" role="list">
          {STEPS.map((s) => (
            <li className="rd-step" key={s.n}>
              <span className="rd-step-bubble" aria-hidden>{s.n}</span>
              <div>
                <h3>{s.title}</h3>
                <p>{s.body}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* Features students love */}
      <section className="rd-features">
        <div className="rd-section-head">
          <span className="rd-kicker">What you get <span className="rd-kicker-am">· የምታገኘው</span></span>
          <h2>Everything you need to pass, in one place.</h2>
        </div>
        <div className="rd-feature-grid">
          {FEATURES.map((f) => (
            <article className="rd-feature" key={f.title}>
              <span className="rd-feature-icon"><Icon name={f.icon} /></span>
              <h3>{f.title}</h3>
              <p>{f.body}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Telegram — where most students are */}
      <section className="rd-telegram">
        <div className="rd-telegram-inner">
          <div className="rd-telegram-copy">
            <span className="rd-kicker">On Telegram <span className="rd-kicker-am">· Telegram irratti</span></span>
            <h2>Most students meet Lomi on Telegram.</h2>
            <p>
              Two out of three Lomi students study right inside Telegram — a daily question, quick
              drills between classes, and Ask Lomi in their pocket. No app to install, no data to spare.
            </p>
            <a className="rd-tg-cta" href="https://t.me/lomistudybot" target="_blank" rel="noopener noreferrer">
              Open @lomistudybot →
            </a>
          </div>
          <div className="rd-telegram-stat">
            <span className="rd-telegram-num">{compact(LOMI_STATS.telegramStudents)}</span>
            <span className="rd-telegram-label">students study on Telegram</span>
          </div>
        </div>
      </section>

      {/* Voices — real testimonials */}
      <section className="rd-voices">
        <div className="rd-section-head">
          <span className="rd-kicker">In their words <span className="rd-kicker-am">· በራሳቸው አነጋገር</span></span>
          <h2>Students aren't quiet about it.</h2>
          <p className="rd-section-sub">Real, unprompted messages from students across Ethiopia — in Amharic, Afaan Oromo, and English.</p>
        </div>
        <div className="rd-voice-grid">
          {LOMI_TESTIMONIALS.map((t) => (
            <figure className="rd-voice" key={t.name + t.quote.slice(0, 12)}>
              <blockquote>{t.quote}</blockquote>
              <figcaption>
                <Avatar name={t.name} size={40} className="rd-voice-avatar" />
                <span className="rd-voice-meta">
                  <span className="rd-voice-name">{t.name}</span>
                  {t.detail && <span className="rd-voice-detail">{t.detail}</span>}
                </span>
              </figcaption>
            </figure>
          ))}
        </div>
        <div className="rd-voices-cta">
          <button onClick={() => navigate('/testimonials')}>Read more student stories →</button>
        </div>
      </section>

      {/* Final CTA */}
      <section className="rd-final">
        <div className="rd-final-card">
          <h2>Your exam is coming. Get ready with Lomi.</h2>
          <p className="rd-final-am">ፈተናህ እየመጣ ነው። ከሎሚ ጋር ተዘጋጅ።</p>
          <p>Free, forever. One tap to start — sign in with Telegram or Gmail.</p>
          <button className="rd-cta-primary rd-final-btn" onClick={() => navigate('/login')}>
            <span className="rd-bubble-mini" aria-hidden />
            Start free · በነጻ ጀምር
            <span className="rd-cta-arrow" aria-hidden>→</span>
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="rd-footer">
        <Logo height={30} withAmharic className="rd-logo" onClick={() => navigate('/')} />
        <nav className="rd-footer-links">
          <button onClick={() => navigate('/about')}>About</button>
          <button onClick={() => navigate('/testimonials')}>Stories</button>
          <button onClick={() => navigate('/blog')}>Blog</button>
          <button onClick={() => navigate('/contact')}>Contact</button>
          <a href="https://t.me/Lomistudybot" target="_blank" rel="noopener noreferrer">Telegram</a>
          <button onClick={() => navigate('/privacy')}>Privacy</button>
        </nav>
        <p className="rd-footer-fine">Built with care in Ethiopia · lomistudy.com</p>
      </footer>

      {/* Sticky mobile CTA */}
      <div className="rd-sticky-cta">
        <button onClick={() => navigate('/login')}>
          <span className="rd-bubble-mini" aria-hidden />
          Start free — one tap
        </button>
      </div>
    </div>
  );
};

export default LandingRedesign;
